import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import useSWRInfinite from 'swr/infinite';
import useSWR from 'swr';
import queryString from 'query-string';

import { AxiosResponse } from 'axios';
import useDebounce from 'hooks/useDebounce';
import { useNotification } from 'hooks/useNotification';
import {
  CompaniesResponse,
  CompanyAttributes,
  PER_PAGE_COUNT,
} from 'lib/models/company';
import { API_ROUTES } from 'lib/api-routes';
import {
  getAxiosPaginatedResponseTotal,
  parseArrayResponse,
  parseAxiosPaginatedResponse,
} from 'lib/utils/parser';
import { apiInstance } from 'lib/utils/axios';
import {
  SponsorshipInvitationPayload,
  UpdateSponsorshipPlanPayload,
} from 'lib/models/sponsorship-plans';
import {
  SponsorCompanyAttributes,
  SponsorshipInitiatorEnum,
  SponsorshipInvitationStateEnum,
  SponsorshipKindEnum,
  sponsorCompaniesResponse,
} from 'lib/models/sponsor-companies';
import { useAuth } from 'lib/providers/AuthProvider';

export interface UseSponsorCompaniesReturnType {
  isLoading: boolean;
  isCompaniesDataLoading: boolean;
  isSponsorsDataLoading: boolean;
  companiesData: CompanyAttributes[];
  searchText: string;
  societySponsorCompanies: SponsorCompanyAttributes[];
  inConversationCompanies: SponsorCompanyAttributes[];
  sponsorCompanyIds: string[];
  sponsorRequestCompanyIds: string[];
  messageCompanyIds: string[];
  fetchMoreData: () => void;
  setSearchText: Dispatch<SetStateAction<string>>;
  setIndustryIds: Dispatch<SetStateAction<string[]>>;
  setCompanySizes: Dispatch<SetStateAction<string[]>>;
  setFilterByVerified: Dispatch<SetStateAction<boolean>>;
  generateGPTMessage: (companyId: string) => Promise<string | undefined>;
  sendMessageToCompany: (
    companyId: string,
    message: string
  ) => Promise<void | string>;
  inviteSponsor: (
    payload: SponsorshipInvitationPayload
  ) => Promise<void | string>;
  removeSponsor: (
    companyId: string,
    isInConversation: boolean
  ) => Promise<void | string>;
  updateSponsorPlan: (
    sponsorShipId: string,
    payload: UpdateSponsorshipPlanPayload,
    inConversation: boolean,
    refreshData?: boolean
  ) => Promise<void | string>;
  onCloseNotification: (id: string) => Promise<void>;
  mutateSponsorshipData: () => Promise<void>;
}

export const useSponsorCompaniesInternal =
  (): UseSponsorCompaniesReturnType => {
    const notificationInstance = useNotification();
    const { isManager } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const [industryIds, setIndustryIds] = useState<string[]>([]);
    const [companySizes, setCompanySizes] = useState<string[]>([]);
    const [filterByVerified, setFilterByVerified] = useState<boolean>(false);
    const [sponsorCompanyIds, setSponsorCompanyIds] = useState<string[]>([]);
    const [sponsorRequestCompanyIds, setSponsorRequestCompanyIds] = useState<
      string[]
    >([]);
    const [messageCompanyIds, setMessageCompanyIds] = useState<string[]>([]);
    const debouncedSearchText = useDebounce(searchText.trim(), 200);

    const fetchCompaniesURI = (pageIndex: number) => {
      const params = {
        employees_count: companySizes,
        industry_ids: industryIds,
        name: debouncedSearchText,
        page: pageIndex + 1,
        per_page: PER_PAGE_COUNT,
        verified: filterByVerified ? true : undefined,
        for_sponsorship: true,
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });

      return `${API_ROUTES.COMPANY_LIST}?${qs}`;
    };

    const {
      data: companiesListResponse,
      error: CompaniesError,
      size,
      setSize,
      isLoading: isCompaniesDataLoading,
    } = useSWRInfinite<AxiosResponse<CompaniesResponse>>(
      fetchCompaniesURI,
      apiInstance.get,
      {
        persistSize: false,
        revalidateOnFocus: false,
        revalidateFirstPage: false,
      }
    );

    const companiesData = useMemo(() => {
      if (!companiesListResponse) return [];
      return parseAxiosPaginatedResponse<CompanyAttributes>(
        companiesListResponse
      );
    }, [companiesListResponse]);

    const {
      data: societySponsorsResponse,
      isLoading: societySponsorsLoading,
      mutate: societySponsorsMutate,
    } = useSWR<AxiosResponse<sponsorCompaniesResponse>>(
      isManager ? API_ROUTES.SOCIETY_SPONSORS : '',
      apiInstance.get,
      {
        revalidateOnFocus: false,
      }
    );

    const societySponsorCompanies = useMemo(() => {
      if (!societySponsorsResponse) return [];
      const data = parseArrayResponse<SponsorCompanyAttributes>(
        societySponsorsResponse.data
      );
      setSponsorCompanyIds(data.map((sponsor) => sponsor.company.data.id));
      return data;
    }, [societySponsorsResponse]);

    const {
      data: inConversationCompaniesResponse,
      isLoading: inConversationCompaniesLoading,
      mutate: inConversationCompaniesMutate,
    } = useSWR<AxiosResponse<sponsorCompaniesResponse>>(
      isManager ? API_ROUTES.SOCIETY_INVITE_SPONSORS : '',
      apiInstance.get,
      {
        revalidateOnFocus: false,
      }
    );

    const inConversationCompanies = useMemo(() => {
      if (!inConversationCompaniesResponse) return [];
      const data = parseArrayResponse<SponsorCompanyAttributes>(
        inConversationCompaniesResponse.data
      );
      const conversationIds: string[] = [];
      const requestIds: string[] = [];
      data.forEach((sponsorData) => {
        const companyId = sponsorData.company.data.id;
        if (
          sponsorData.kind === SponsorshipKindEnum.request &&
          sponsorData.initiator === SponsorshipInitiatorEnum.StudentSociety &&
          sponsorData.state !== SponsorshipInvitationStateEnum.rejected
        ) {
          requestIds.push(companyId);
        } else if (sponsorData.kind === SponsorshipKindEnum.conversation) {
          conversationIds.push(companyId);
        }
      });
      setMessageCompanyIds(conversationIds);
      setSponsorRequestCompanyIds(requestIds);
      return data;
    }, [inConversationCompaniesResponse]);

    const isSponsorsDataLoading = useMemo(
      () => inConversationCompaniesLoading || societySponsorsLoading,
      [inConversationCompaniesLoading, societySponsorsLoading]
    );

    const fetchMoreData = () => {
      if (
        !isCompaniesDataLoading &&
        !CompaniesError &&
        companiesData.length === size * PER_PAGE_COUNT
      ) {
        const total = getAxiosPaginatedResponseTotal(companiesListResponse);
        const hasMoreRecords = total > size * PER_PAGE_COUNT;
        if (hasMoreRecords) {
          setSize(size + 1);
        }
      }
    };

    const generateGPTMessage = async (company_id: string) => {
      try {
        const response: { data: { suggestions: string } } =
          await apiInstance.post(API_ROUTES.SOCIETY_AI_MESSAGE_SUGGESTION, {
            company_id,
          });
        return response.data.suggestions;
      } catch (error) {
        notificationInstance.handleExceptionError(error as Error);
      }
    };

    const sendMessageToCompany = async (companyId: string, message: string) => {
      const path = API_ROUTES.SOCIETY_MESSAGE_SPONSOR;
      const body = {
        message: {
          value: message,
        },
        company_id: companyId,
      };

      try {
        setIsLoading(true);
        const response = await apiInstance.post(path, body);
        await inConversationCompaniesMutate();
        notificationInstance.success({
          title: 'Success',
          message: 'Message sent.',
        });
        return response.data.success;
      } catch (error) {
        notificationInstance.error(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    const inviteSponsor = async (payload: SponsorshipInvitationPayload) => {
      try {
        setIsLoading(true);
        const response = await apiInstance.post(
          API_ROUTES.SOCIETY_INVITE_SPONSORS,
          payload
        );
        await inConversationCompaniesMutate();
        notificationInstance.success({
          title: 'Success',
          message: 'Sponsorship invitation sent successfully.',
        });
        return response.data;
      } catch (error) {
        notificationInstance.error(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    const removeSponsor = async (
      companyId: string,
      isInConversation: boolean
    ) => {
      try {
        setIsLoading(true);
        const path = isInConversation
          ? `${API_ROUTES.SOCIETY_INVITE_SPONSORS}/${companyId}`
          : `${API_ROUTES.SOCIETY_SPONSORS}/${companyId}`;
        const response = await apiInstance.delete(path);
        if (isInConversation) {
          await inConversationCompaniesMutate();
        } else {
          await societySponsorsMutate();
        }
        notificationInstance.success({
          title: 'Success',
          message: 'Sponsor removed successfully.',
        });
        return response.data.notice;
      } catch (error) {
        notificationInstance.error(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    const updateSponsorPlan = async (
      sponsorShipId: string,
      payload: UpdateSponsorshipPlanPayload,
      inConversation: boolean,
      refreshData?: boolean
    ) => {
      const path = `${
        inConversation
          ? API_ROUTES.SOCIETY_INVITE_SPONSORS
          : API_ROUTES.SOCIETY_SPONSORS
      }/${sponsorShipId}`;

      try {
        setIsLoading(true);
        await apiInstance.patch(path, payload);
        if (refreshData) {
          await mutateSponsorshipData();
        } else {
          inConversation
            ? await inConversationCompaniesMutate()
            : await societySponsorsMutate();
        }
        notificationInstance.success({
          title: 'Success',
          message: 'Plan Change request is sent successfully.',
        });
        return 'Success';
      } catch (error) {
        notificationInstance.error(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    const onCloseNotification = async (id: string) => {
      const body = {
        sponsorship_invitation: {
          student_society_seen: true,
        },
      };
      try {
        setIsLoading(true);
        await apiInstance.patch(
          `${API_ROUTES.SOCIETY_INVITE_SPONSORS}/${id}`,
          body
        );
        await inConversationCompaniesMutate();
      } catch (error) {
        notificationInstance.error(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    const mutateSponsorshipData = async () => {
      await inConversationCompaniesMutate();
      await societySponsorsMutate();
    };

    return {
      isLoading,
      isCompaniesDataLoading,
      isSponsorsDataLoading,
      companiesData,
      searchText,
      societySponsorCompanies,
      inConversationCompanies,
      sponsorCompanyIds,
      sponsorRequestCompanyIds,
      messageCompanyIds,
      fetchMoreData,
      setSearchText,
      setIndustryIds,
      setCompanySizes,
      setFilterByVerified,
      generateGPTMessage,
      sendMessageToCompany,
      inviteSponsor,
      removeSponsor,
      updateSponsorPlan,
      onCloseNotification,
      mutateSponsorshipData,
    };
  };
